import { useEffect, useState } from 'react';
import { api, isSampleMode } from './api/client';
import { isAuthed, isProgrammeAdmin } from './auth/cognito';
import { sampleOrganisations } from './sample';
import { useStore } from './store';
import type { OrgSummary } from './types';

/**
 * Loads the Organisations index (GET /campaigns — one campaign == one organisation) for the
 * admin list screen. Sample mode renders the bundled fixture so the screen is demonstrable
 * without a backend. Non-admins are guarded before the request so they never trip the API's
 * 403 (which the client treats as an auth bounce); the backend still enforces it.
 */
export interface OrganisationsState {
  organisations: OrgSummary[];
  loading: boolean;
  error: string | null;
}

export function useOrganisations(): OrganisationsState {
  const live = !isSampleMode && isAuthed();
  const [organisations, setOrganisations] = useState<OrgSummary[]>(live ? [] : sampleOrganisations);
  const [loading, setLoading] = useState(live);
  const [error, setError] = useState<string | null>(null);
  // Orgs created/edited this session (sample-mode persistence + optimistic create).
  const extraOrgs = useStore((s) => s.extraOrgs);

  useEffect(() => {
    if (!live) {
      setOrganisations(sampleOrganisations);
      setLoading(false);
      return;
    }
    if (!isProgrammeAdmin()) {
      setError('Programme admin access required.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listOrganisations()
      .then(({ organisations }) => {
        if (cancelled) return;
        setOrganisations(organisations);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(/403/.test(e.message) ? 'Programme admin access required.' : 'Could not load organisations.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [live]);

  // Merge any session-created orgs ahead of the loaded list (deduped by id).
  const merged = extraOrgs.length
    ? [...extraOrgs, ...organisations.filter((o) => !extraOrgs.some((e) => e.campaignId === o.campaignId))]
    : organisations;
  return { organisations: merged, loading, error };
}
